import { Permission } from "@/model/RootStore"

export enum PermissionSection {
	MapLayers = "mapLayers",
	Opportunities = "opportunities",
	TransactionsParcels = "transactionsParcels",
	DevelopmentPotential = "developmentPotential",
	Projects = "projects",
	CrmBuyers = "crmBuyers",
	Communication = "communication",
	Administration = "administration",
	Analytics = "analytics",
	Other = "other",
}

const sectionPermissions: [PermissionSection, Permission[]][] = [
	[
		PermissionSection.MapLayers,
		[
			Permission.AgriculturalZonesRead,
			Permission.CityPvDocumentsRead,
			Permission.CompletedAssessmentsRead,
			Permission.EndangeredSpeciesRead,
			Permission.FloodableZonesRead,
			Permission.LandUseRead,
			Permission.PlanningPolicyRead,
			Permission.ProspectsRead,
			Permission.SoilCapabilityRead,
			Permission.TransportationRead,
			Permission.UrbanPerimetersRead,
			Permission.UrbanPrioritiesRead,
			Permission.UrbanPrioritiesWrite,
			Permission.WetlandsRead,
			Permission.ZoningRead,
		],
	],
	[
		PermissionSection.Opportunities,
		[
			Permission.OpportunityRead,
			Permission.OpportunityWrite,
			Permission.OpportunityAdmin,
			Permission.OpportunityClientNoteWrite,
			Permission.OpportunityOffmarketClear,
			Permission.OpportunitySensitiveRead,
			Permission.OpportunitySensitiveWrite,
			Permission.OpportunityTargetMove,
			Permission.OpportunityTargetWrite,
			Permission.CreateTestOpportunity,
		],
	],
	[
		PermissionSection.TransactionsParcels,
		[
			Permission.ComparableAnalysisRead,
			Permission.ComparableAnalysisWrite,
			Permission.TransactionRead,
			Permission.TransactionWrite,
			Permission.TransactionSensitiveRead,
			Permission.ParcelExport,
			Permission.ParcelReportExport,
			Permission.ParcelSensitiveRead,
		],
	],
	[PermissionSection.DevelopmentPotential, [Permission.DevPotentialRead, Permission.DevPotentialWrite]],
	[PermissionSection.Projects, [Permission.ProjectList, Permission.ProjectWrite]],
	[PermissionSection.CrmBuyers, [Permission.BuyerRead, Permission.ContactResearchRead, Permission.CrmRead]],
	[PermissionSection.Communication, [Permission.EmailCreate, Permission.NotificationCreate, Permission.SmsCreate]],
	[
		PermissionSection.Administration,
		[
			Permission.AuthorizationRead,
			Permission.AuthorizationWrite,
			Permission.DocumentStoreUpload,
			Permission.ServiceTokenWrite,
			Permission.UserCreate,
		],
	],
	[PermissionSection.Analytics, [Permission.DashboardRead]],
]

const sectionByPermissionName = new Map<string, PermissionSection>(
	sectionPermissions.flatMap(([section, permissions]) => permissions.map(name => [name, section] as const))
)

const sectionOrder = [...sectionPermissions.map(([section]) => section), PermissionSection.Other]

export function groupPermissionsBySection<T extends { name: string }>(
	permissions: T[]
): { section: PermissionSection; permissions: T[] }[] {
	return sectionOrder
		.map(section => ({
			section,
			permissions: permissions
				.filter(permission => (sectionByPermissionName.get(permission.name) ?? PermissionSection.Other) === section)
				.sort((a, b) => a.name.localeCompare(b.name)),
		}))
		.filter(group => group.permissions.length > 0)
}
